import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Input, Fieldset, Legend, Text } from '../components/ui'
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/16/solid'
import { useAuthContext } from '../contexts/AuthContext'

const Login: React.FC = () => {
  const navigate = useNavigate()
  const { signIn, loading, isAuthenticated } = useAuthContext()
  const [loginError, setLoginError] = useState<string | null>(null)
  const [showPassword, setShowPassword] = useState(false)
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  })

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true })
    }
  }, [isAuthenticated, navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    setLoginError(null)
    const result = await signIn(formData.email, formData.password)

    if (result.success) {
      navigate('/dashboard')
    } else {
      setLoginError(result.error || 'Erro ao fazer login')
      console.error('Erro no login:', result.error)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleForgotPassword = () => {
    navigate('/forgot-password')
  }

  const handleRegister = () => {
    navigate('/register')
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-gray-50 dark:bg-gray-900">
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -left-32 -top-32 h-96 w-96 rounded-full bg-primary-500/10 blur-3xl" />
        <div className="absolute -bottom-32 -right-32 h-96 w-96 rounded-full bg-primary-400/10 blur-3xl" />
        <div
          className="absolute inset-0 opacity-[0.35] dark:opacity-[0.15]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 1px 1px, rgb(0 139 149 / 0.12) 1px, transparent 0)',
            backgroundSize: '28px 28px',
          }}
        />
      </div>

      <div className="relative flex min-h-screen items-center justify-center px-4 py-10 sm:px-6 lg:px-8">
        <div className="w-full max-w-[46.2rem] animate-fade-in">
          <div className="overflow-hidden rounded-2xl bg-white shadow-strong ring-1 ring-gray-200/80 dark:bg-gray-800 dark:ring-white/10">
            <div className="grid lg:grid-cols-2 lg:items-stretch">
              <div className="relative flex flex-col items-center justify-center overflow-hidden border-b border-gray-100 bg-[#008b95] px-4 py-10 sm:px-6 sm:py-12 lg:border-b-0 lg:border-r lg:py-10 dark:border-gray-700/80">
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-0 animate-robot-glow motion-reduce:animate-none"
                  style={{
                    background:
                      'radial-gradient(circle at 50% 40%, rgba(255,255,255,0.25) 0%, transparent 65%)',
                  }}
                />
                <div className="relative w-full max-w-[240px] animate-robot-boot opacity-0 motion-reduce:animate-none motion-reduce:opacity-100 sm:max-w-[280px] lg:max-w-none">
                  <img
                    src="/Logo_QG_PASI-01.png"
                    alt="QG PASI Logo"
                    className="h-auto w-full object-contain"
                  />
                  <span className="pointer-events-none absolute left-[33%] top-[23%] h-[2.8%] w-[7%] origin-center rounded-full bg-[#008b95] animate-robot-blink motion-reduce:hidden" />
                  <span className="pointer-events-none absolute left-[58%] top-[23%] h-[2.8%] w-[7%] origin-center rounded-full bg-[#008b95] animate-robot-blink motion-reduce:hidden" />
                </div>
              </div>

              <div className="flex flex-col justify-center px-5 py-8 sm:px-8 sm:py-10">
                {loginError && (
                  <div className="mb-6 rounded-lg border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700 dark:border-error-800 dark:bg-error-950/40 dark:text-error-300">
                    {loginError}
                  </div>
                )}

                <form className="space-y-5" onSubmit={handleSubmit}>
                  <Fieldset>
                    <Legend>Endereço de email</Legend>
                    <div className="mt-2">
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="Digite seu email"
                        className="focus:ring-primary-500/25 dark:focus:ring-primary-400/20"
                        required
                        disabled={loading}
                      />
                    </div>
                  </Fieldset>

                  <Fieldset>
                    <Legend>Senha</Legend>
                    <div className="relative mt-2">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        placeholder="Digite sua senha"
                        className="pr-10 focus:ring-primary-500/25 dark:focus:ring-primary-400/20"
                        required
                        disabled={loading}
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 flex items-center pr-3"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                        disabled={loading}
                      >
                        {showPassword ? (
                          <EyeSlashIcon className="h-4 w-4 text-gray-400" />
                        ) : (
                          <EyeIcon className="h-4 w-4 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </Fieldset>

                  <div className="flex items-center justify-between pt-1">
                    <div className="flex items-center">
                      <input
                        id="remember-me"
                        name="remember-me"
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300 text-primary-500 focus:ring-primary-500/25"
                        disabled={loading}
                      />
                      <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900 dark:text-white">
                        Lembrar de mim
                      </label>
                    </div>

                    <button
                      type="button"
                      onClick={handleForgotPassword}
                      className="text-sm font-medium text-primary-500 transition-colors hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                      disabled={loading}
                    >
                      Esqueceu sua senha?
                    </button>
                  </div>

                  <Button
                    type="submit"
                    className="w-full border-0 bg-primary-500 text-white shadow-sm ring-0 hover:bg-primary-700 focus:ring-primary-500/25 disabled:bg-primary-500/50"
                    size="lg"
                    disabled={loading}
                  >
                    {loading ? 'Entrando...' : 'Entrar'}
                  </Button>

                  <div className="border-t border-gray-100 pt-5 text-center dark:border-gray-700/80">
                    <Text size="sm" color="muted">
                      Não tem uma conta?{' '}
                      <button
                        type="button"
                        onClick={handleRegister}
                        className="font-medium text-primary-500 transition-colors hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                        disabled={loading}
                      >
                        Registre-se
                      </button>
                    </Text>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Login
